
import React, { useState, useEffect } from 'react';
import { Page } from '../types';
import { useAuth } from '../context/AuthContext';
import {
    ChartBarIcon,
    UsersIcon,
    ShoppingBagIcon,
    ShieldExclamationIcon,
    TargetIcon,
    ExclamationTriangleIcon
} from '../assets/icons';

import AdminUsers from '../components/AdminUsers';
import AdminCompliance from '../components/AdminCompliance';

// Mock Data for the Live Feed
const MOCK_AI_LOGS = [
    { id: 1, time: 'Agora', agent: 'Sr. Nall', action: 'Negociando Glock G19', status: 'active', profit: '+15%' },
    { id: 2, time: '2s atrás', agent: 'Compliance Bot', action: 'Analisando doc. CR #9928', status: 'warning', profit: '' },
    { id: 3, time: '5s atrás', agent: 'Sr. Nall', action: 'Fechamento venda Kit Despachante', status: 'success', profit: '+R$ 450' },
    { id: 4, time: '12s atrás', agent: 'System', action: 'Alerta de estoque baixo: Munição 9mm', status: 'danger', profit: '' },
    { id: 5, time: '30s atrás', agent: 'Sr. Nall', action: 'Prospect detectado na Home', status: 'info', profit: '' },
];

interface AdminPanelPageProps {
    onNavigate: (page: Page) => void;
}

const AdminPanelPage: React.FC<AdminPanelPageProps> = ({ onNavigate }) => {
    const { isAuthenticated } = useAuth();
    const [currentTime, setCurrentTime] = useState(new Date());
    const [currentView, setCurrentView] = useState<'dashboard' | 'users' | 'compliance'>('dashboard');

    useEffect(() => {
        if (!isAuthenticated) {
            onNavigate('login');
        }
    }, [isAuthenticated, onNavigate]);

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    if (!isAuthenticated) return null;

    return (
        <div className="bg-gray-900 min-h-screen text-white font-mono p-6">
            {/* Top Bar */}
            <div className="flex justify-between items-center mb-8 border-b border-gray-800 pb-4">
                <div className="flex items-center gap-4">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_#22c55e]"></div>
                    <h1 className="text-2xl font-black tracking-widest uppercase text-gray-100 flex items-center gap-2">
                        <ShieldExclamationIcon className="h-6 w-6 text-arsenal-red" />
                        Command Center <span className="text-gray-600 text-sm align-super">v2.1</span>
                    </h1>
                </div>

                {/* Navigation Tabs */}
                <div className="flex bg-gray-800 rounded-lg p-1 gap-1">
                    <button
                        onClick={() => setCurrentView('dashboard')}
                        className={`px-4 py-1.5 rounded text-xs font-bold uppercase transition-all ${currentView === 'dashboard' ? 'bg-gray-700 text-white shadow' : 'text-gray-500 hover:text-gray-300'}`}
                    >
                        Visão Geral
                    </button>
                    <button
                        onClick={() => setCurrentView('users')}
                        className={`px-4 py-1.5 rounded text-xs font-bold uppercase transition-all ${currentView === 'users' ? 'bg-blue-900/50 text-blue-200 shadow' : 'text-gray-500 hover:text-gray-300'}`}
                    >
                        Esquadrão
                    </button>
                    <button
                        onClick={() => setCurrentView('compliance')}
                        className={`px-4 py-1.5 rounded text-xs font-bold uppercase transition-all ${currentView === 'compliance' ? 'bg-green-900/50 text-green-200 shadow' : 'text-gray-500 hover:text-gray-300'}`}
                    >
                        Compliance
                    </button>
                </div>

                <div className="flex items-center gap-6 text-sm text-gray-400">
                    <div className="font-bold text-white text-lg">
                        {currentTime.toLocaleTimeString('pt-BR')}
                    </div>
                </div>
            </div>

            {/* Content Area */}
            {currentView === 'users' ? (
                <AdminUsers />
            ) : currentView === 'compliance' ? (
                <AdminCompliance />
            ) : (
                /* Dashboard View (Original Content) */
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 animate-fade-in">

                    {/* Stats Cards (Top Row) */}
                    <div className="lg:col-span-4 grid grid-cols-1 md:grid-cols-4 gap-4 mb-2">
                        <StatCard
                            title="Vendas Hoje"
                            value="R$ 142.500"
                            change="+12%"
                            icon={<ShoppingBagIcon className="w-6 h-6 text-blue-400" />}
                            trend="up"
                        />
                        <StatCard
                            title="Usuários Ativos"
                            value="3,402"
                            change="+5%"
                            icon={<UsersIcon className="w-6 h-6 text-purple-400" />}
                            trend="up"
                        />
                        <StatCard
                            title="Taxa de Conversão da IA"
                            value="8.4%"
                            change="+1.2%"
                            icon={<TargetIcon className="w-6 h-6 text-green-400" />}
                            trend="up"
                        />
                        <StatCard
                            title="Alertas de Risco"
                            value="3"
                            change="-2"
                            icon={<ExclamationTriangleIcon className="w-6 h-6 text-arsenal-red" />}
                            trend="down"
                            isDanger
                        />
                    </div>

                    {/* Main Map / Visualization Area (Big Block) */}
                    <div className="lg:col-span-3 bg-gray-800/30 rounded-2xl border border-gray-700 p-6 relative overflow-hidden min-h-[500px]">
                        <div className="absolute inset-0 bg-[url('https://upload.wikimedia.org/wikipedia/commons/b/b3/Map_of_Brazil_with_flag.svg')] bg-cover bg-center opacity-5 grayscale mix-blend-overlay"></div>
                        <div className="relative z-10">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                    <GlobeAmericasIcon className="w-5 h-5 text-gray-400" />
                                    Monitoramento Territorial
                                </h3>
                                <div className="flex gap-2">
                                    <button className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs font-bold transition-colors">Vendas</button>
                                    <button className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded text-xs font-bold text-gray-400 transition-colors">Ameaças</button>
                                </div>
                            </div>

                            {/* Animated Mock Content for Map */}
                            <div className="relative h-[400px] w-full border border-gray-700/50 rounded-lg bg-gray-900/50 flex items-center justify-center overflow-hidden">
                                {/* Radar Scan Effect */}
                                <div className="absolute inset-0 z-0">
                                    <div className="absolute top-1/2 left-1/2 w-[600px] h-[600px] -translate-x-1/2 -translate-y-1/2 border border-green-500/10 rounded-full animate-pulse"></div>
                                    <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] -translate-x-1/2 -translate-y-1/2 border border-green-500/20 rounded-full"></div>
                                    <div className="absolute top-1/2 left-1/2 w-full h-[2px] bg-gradient-to-r from-transparent via-green-500/20 to-transparent -translate-x-1/2 -translate-y-1/2 animate-[spin_4s_linear_infinite]"></div>
                                </div>

                                {/* Map Dots */}
                                <MapDot top="30%" left="60%" label="São Paulo - R$ 45k" delay="0s" />
                                <MapDot top="40%" left="55%" label="Paraná - R$ 22k" delay="1.2s" color="blue" />
                                <MapDot top="20%" left="65%" label="Bahia - R$ 12k" delay="2.5s" color="blue" />
                                <MapDot top="15%" left="45%" label="Mato Grosso - R$ 85k" delay="0.5s" color="amber" size="large" />

                                <div className="absolute bottom-4 left-4 p-3 bg-black/80 backdrop-blur rounded border border-gray-700 text-xs">
                                    <div className="flex items-center gap-2 mb-1"><span className="w-2 h-2 rounded-full bg-arsenal-red"></span> Negociações de Alto Valor</div>
                                    <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-500"></span> Vendas Padrão</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* AI Activity Feed (Right Column) */}
                    <div className="lg:col-span-1 bg-gray-800/30 rounded-2xl border border-gray-700 p-0 flex flex-col h-[500px]">
                        <div className="p-4 border-b border-gray-700 bg-gray-800/50 rounded-t-2xl">
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                Live AI Feed
                            </h3>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 font-mono text-xs">
                            {MOCK_AI_LOGS.map((log) => (
                                <div key={log.id} className="border-l-2 border-gray-700 pl-3 py-1 relative">
                                    <div className={`absolute -left-[5px] top-2 w-2 h-2 rounded-full ${getStatusColor(log.status)}`}></div>
                                    <div className="flex justify-between text-gray-500 mb-1">
                                        <span>{log.time}</span>
                                        <span className="font-bold text-gray-300">{log.agent}</span>
                                    </div>
                                    <p className="text-gray-300">{log.action}</p>
                                    {log.profit && <span className="text-green-400 font-bold block mt-1">{log.profit}</span>}
                                </div>
                            ))}
                            {/* Fake "typing" loader */}
                            <div className="flex gap-1 items-center text-gray-600 mt-4">
                                <span className="animate-bounce">.</span>
                                <span className="animate-bounce delay-100">.</span>
                                <span className="animate-bounce delay-200">.</span>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Row - Status & Controls */}
                    <div className="lg:col-span-4 grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-gray-800/30 rounded-xl p-4 border border-gray-700 flex items-center justify-between">
                            <div>
                                <h4 className="text-gray-400 text-xs uppercase">Integridade do Sistema</h4>
                                <span className="text-xl font-bold text-green-400">99.9%</span>
                            </div>
                            <ChartBarIcon className="w-8 h-8 text-gray-600 opacity-50" />
                        </div>
                        <div className="bg-gray-800/30 rounded-xl p-4 border border-gray-700 flex items-center justify-between">
                            <div>
                                <h4 className="text-gray-400 text-xs uppercase">Estoque em Trânsito</h4>
                                <span className="text-xl font-bold text-white">42 Unidades</span>
                            </div>
                            <ShoppingBagIcon className="w-8 h-8 text-gray-600 opacity-50" />
                        </div>
                        <div className="bg-gray-800/30 rounded-xl p-4 border border-gray-700 flex items-center justify-between opacity-50 cursor-not-allowed">
                            <div>
                                <h4 className="text-gray-400 text-xs uppercase">Modo de Crise</h4>
                                <span className="text-xl font-bold text-gray-500">DESATIVADO</span>
                            </div>
                            <ShieldExclamationIcon className="w-8 h-8 text-gray-600" />
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
};

// --- Sub-components for internal use ---

const StatCard = ({ title, value, change, icon, trend, isDanger = false }: any) => (
    <div className="bg-gray-800/40 rounded-xl p-5 border border-gray-700 hover:border-gray-600 transition-colors backdrop-blur-sm">
        <div className="flex justify-between items-start mb-4">
            <div className={`p-2 rounded-lg ${isDanger ? 'bg-red-900/20' : 'bg-gray-900/50'}`}>
                {icon}
            </div>
            <span className={`text-xs font-bold px-2 py-1 rounded ${trend === 'up' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                }`}>
                {change}
            </span>
        </div>
        <h3 className="text-gray-400 text-xs uppercase tracking-wider mb-1">{title}</h3>
        <p className="text-2xl font-black text-white">{value}</p>
    </div>
);

const MapDot = ({ top, left, label, delay, color = "red", size = "normal" }: any) => {
    const colorClass = color === "red" ? "bg-arsenal-red" : color === "blue" ? "bg-blue-500" : "bg-amber-500";
    const shadowClass = color === "red" ? "shadow-red-500/50" : color === "blue" ? "shadow-blue-500/50" : "shadow-amber-500/50";
    const sizeClass = size === "large" ? "w-4 h-4" : "w-3 h-3";

    return (
        <div className="absolute group cursor-pointer" style={{ top, left }}>
            <div className={`relative ${sizeClass} rounded-full ${colorClass} shadow-lg ${shadowClass} animate-pulse`} style={{ animationDelay: delay }}>
                <div className={`absolute -inset-2 rounded-full ${colorClass} opacity-20 animate-ping`} style={{ animationDelay: delay }}></div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-black/90 text-white text-[10px] font-bold rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none border border-gray-700">
                {label}
            </div>
        </div>
    );
};

const getStatusColor = (status: string) => {
    switch (status) {
        case 'active': return 'bg-blue-500';
        case 'success': return 'bg-green-500';
        case 'warning': return 'bg-amber-500';
        case 'danger': return 'bg-red-500';
        default: return 'bg-gray-500';
    }
};

// Helper Icon for this page specifically if needed, likely imported from icons.tsx
const GlobeAmericasIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
    </svg>
);

export default AdminPanelPage;
